import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import {
  AlertTriangle,
  Shield,
  Lock,
  Zap,
  Building2,
  Crown,
  Plus,
  Settings,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { formatFeatureName } from '../../utils/featureNames';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FeatureGovernance = () => {
  const [tiers, setTiers] = useState([]);
  const [governance, setGovernance] = useState(null);
  const [systemFeatures, setSystemFeatures] = useState([]);
  const [customFeatures, setCustomFeatures] = useState([]);
  const [systemAddons, setSystemAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewFeatureDialog, setShowNewFeatureDialog] = useState(false);
  const [showEditFeatureDialog, setShowEditFeatureDialog] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [showNewAddonDialog, setShowNewAddonDialog] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Governance filter state
  const [governanceFilter, setGovernanceFilter] = useState('all'); // 'all', 'unassigned', 'none', 'professional_minimum', 'business_minimum', 'enterprise_only'
  
  // Accordion state for expandable pages
  const [expandedPages, setExpandedPages] = useState(new Set());
  
  // Accordion state for expandable tabs with sub-tabs
  const [expandedTabs, setExpandedTabs] = useState(new Set());
  
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    featureName: null
  });
  const [deleteTierConfirmation, setDeleteTierConfirmation] = useState({
    isOpen: false,
    tierName: null,
    tierDisplayName: null
  });
  
  // Rescan state
  const [isRescanning, setIsRescanning] = useState(false);
  
  // New feature form
  const [newFeature, setNewFeature] = useState({
    name: '',
    display_name: '',
    description: '',
    minimum_tier: 'starter',
    governance_level: 'none'
  });

  // New tier form
  const [showCreateTierDialog, setShowCreateTierDialog] = useState(false);
  const [newTierForm, setNewTierForm] = useState({
    name: '',
    display_name: '',
    description: '',
    price_monthly: 0,
    price_annual: 0,
    trial_enabled: false,
    trial_days: 0,
    features: []
  });

  // Helper function to get auth token from cookies or localStorage
  const getAuthToken = () => {
    return Cookies.get('enakid_token') || localStorage.getItem('token') || localStorage.getItem('enakid_token');
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const token = getAuthToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [tiersResponse, governanceResponse, systemFeaturesResponse, customFeaturesResponse, addonsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/subscription-tiers`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/feature-governance`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/system-features`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/custom-features`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/add-on-services`, { headers })
      ]);
      
      setTiers(tiersResponse.data || []);
      setGovernance(governanceResponse.data || {});
      setSystemFeatures(systemFeaturesResponse.data?.features || []);
      setCustomFeatures(customFeaturesResponse.data || []);
      setSystemAddons(addonsResponse.data || []);
      
      console.log(`Loaded ${systemFeaturesResponse.data?.total || 0} system features`);
      console.log(`Loaded ${customFeaturesResponse.data?.length || 0} custom features`);
      console.log(`Loaded ${addonsResponse.data?.length || 0} system addons`);
      
    } catch (error) {
      console.error('Error fetching governance data:', error);
      toast.error('Failed to load feature governance data');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to generate dynamic governance options based on actual tier names
  const getGovernanceOptions = () => {
    // Sort tiers by a hierarchy (starter -> professional -> business -> enterprise)
    const tierHierarchy = ['starter', 'professional', 'business', 'enterprise'];
    const sortedTiers = [...tiers].sort((a, b) => {
      const indexA = tierHierarchy.indexOf(a.name.toLowerCase());
      const indexB = tierHierarchy.indexOf(b.name.toLowerCase());
      return indexA - indexB;
    });

    const options = [];
    
    // Add "Unassign" option first - removes from all governance
    options.push({
      value: 'unassign',
      label: '❌ Unassign (Remove from all tiers)'
    });
    
    // Add first tier as "X Minimum" (matches filter button)
    if (sortedTiers.length > 0) {
      const firstTier = sortedTiers[0];
      const tierName = firstTier.display_name || firstTier.name.charAt(0).toUpperCase() + firstTier.name.slice(1);
      options.push({
        value: 'starter_minimum',
        label: `${tierName} Minimum (All Tiers)`
      });
    }

    // Generate options dynamically from remaining tiers
    for (let i = 1; i < sortedTiers.length; i++) {
      const tier = sortedTiers[i];
      const tierName = tier.display_name || tier.name.charAt(0).toUpperCase() + tier.name.slice(1);
      
      if (i === sortedTiers.length - 1) {
        // Last tier - "X Only"
        options.push({
          value: `${tier.name}_only`,
          label: `${tierName} Only`
        });
      } else {
        // Middle tiers - "X Minimum"
        options.push({
          value: `${tier.name}_minimum`,
          label: `${tierName} Minimum`
        });
      }
    }

    return options;
  };

  // Helper function to generate dynamic filter buttons based on actual tiers
  const getGovernanceFilterButtons = () => {
    // Sort tiers by hierarchy
    const tierHierarchy = ['starter', 'professional', 'business', 'enterprise'];
    const sortedTiers = [...tiers].sort((a, b) => {
      const indexA = tierHierarchy.indexOf(a.name.toLowerCase());
      const indexB = tierHierarchy.indexOf(b.name.toLowerCase());
      return indexA - indexB;
    });

    const filterButtons = [
      { value: 'all', label: 'All Features' },
      { value: 'unassigned', label: 'Unassigned' }
    ];

    // Add filter for each tier based on database tiers
    sortedTiers.forEach((tier, index) => {
      const tierName = tier.display_name || tier.name.charAt(0).toUpperCase() + tier.name.slice(1);
      
      if (index === 0) {
        // First tier (Starter) - show as "Starter Minimum"
        filterButtons.push({
          value: 'starter_minimum',
          label: `${tierName} Minimum`
        });
      } else if (index === sortedTiers.length - 1) {
        // Last tier (Enterprise) - show as "Enterprise Only"
        filterButtons.push({
          value: `${tier.name}_only`,
          label: `${tierName} Only`
        });
      } else {
        // Middle tiers - show as "Professional Minimum", "Business Minimum"
        filterButtons.push({
          value: `${tier.name}_minimum`,
          label: `${tierName} Minimum`
        });
      }
    });

    return filterButtons;
  };

  const getTierIcon = (tierName) => {
    switch (tierName) {
      case 'starter': return <Eye className="h-4 w-4" />;
      case 'professional': return <Zap className="h-4 w-4" />;
      case 'business': return <Building2 className="h-4 w-4" />;
      case 'enterprise': return <Crown className="h-4 w-4" />;
      default: return <Settings className="h-4 w-4" />;
    }
  };

  const getTierColor = (tierName) => {
    switch (tierName) {
      case 'starter': return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'professional': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'business': return 'bg-green-100 text-green-800 border-green-300';
      case 'enterprise': return 'bg-purple-100 text-purple-800 border-purple-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getGovernanceLevel = (featureName) => {
    if (governance?.feature_restrictions?.enterprise_only?.includes(featureName)) {
      return { level: 'enterprise_only', color: 'bg-purple-100 text-purple-800 border-purple-300', icon: <Crown className="h-3 w-3" /> };
    }
    if (governance?.feature_restrictions?.business_minimum?.includes(featureName)) {
      return { level: 'business_minimum', color: 'bg-green-100 text-green-800 border-green-300', icon: <Building2 className="h-3 w-3" /> };
    }
    if (governance?.feature_restrictions?.professional_minimum?.includes(featureName)) {
      return { level: 'professional_minimum', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: <Zap className="h-3 w-3" /> };
    }
    // Check if feature is explicitly in "none" (free_minimum) - though this doesn't exist yet in backend
    // For now, we'll treat any feature not in the above as unassigned
    return { level: 'unassigned', color: 'bg-gray-100 text-gray-600 border-gray-300', icon: <AlertTriangle className="h-3 w-3" /> };
  };


  // Get display name for a feature (from systemFeatures if available, otherwise format the name)
  const getFeatureDisplayName = (featureName) => {
    // Try to find the feature in systemFeatures to get its proper display_name
    const feature = systemFeatures.find(f => f.name === featureName);
    if (feature && feature.display_name) {
      // Check if it has proper spacing (contains spaces)
      if (feature.display_name.includes(' ')) {
        return feature.display_name;
      }
      // If display_name is malformed (all lowercase, no spaces), use the feature name instead
      // Example: "costcentermanagement" -> use "cost_center_management" instead
      if (feature.display_name.toLowerCase() === feature.display_name && !feature.display_name.includes('_')) {
        // It's all lowercase with no delimiters, use the snake_case name instead
        return formatFeatureName(feature.name);
      }
      // Otherwise format the display_name (handles PascalCase, camelCase)
      return formatFeatureName(feature.display_name);
    }
    // Fallback to formatFeatureName if not found
    return formatFeatureName(featureName);
  };

  // Organize features into hierarchical structure (pages with child tabs, sub-pages, and sub-tabs)
  const organizeFeatureHierarchy = (features) => {
    const pages = [];
    const childrenByParent = {};
    const subTabsByParent = {};
    
    // First pass: separate pages, tabs/sub_pages, and sub_tabs
    features.forEach(feature => {
      if (feature.granularity === 'page') {
        pages.push({
          ...feature,
          children: []
        });
      } else if (feature.granularity === 'sub_tab' && feature.parent_feature) {
        // Sub-tabs are children of tabs
        if (!subTabsByParent[feature.parent_feature]) {
          subTabsByParent[feature.parent_feature] = [];
        }
        subTabsByParent[feature.parent_feature].push(feature);
      } else if ((feature.granularity === 'tab' || feature.granularity === 'sub_page') && feature.parent_feature) {
        // Tabs and sub_pages are children of pages
        if (!childrenByParent[feature.parent_feature]) {
          childrenByParent[feature.parent_feature] = [];
        }
        childrenByParent[feature.parent_feature].push({
          ...feature,
          children: [] // Will be populated with sub-tabs
        });
      }
    });
    
    // Second pass: attach sub-tabs to their parent tabs
    Object.keys(childrenByParent).forEach(parentName => {
      childrenByParent[parentName].forEach(tab => {
        if (subTabsByParent[tab.name]) {
          tab.children = subTabsByParent[tab.name];
        }
      });
    });
    
    // Third pass: attach children (tabs and sub_pages) to their parent pages
    pages.forEach(page => {
      if (childrenByParent[page.name]) {
        page.children = childrenByParent[page.name];
      }
    });
    
    return pages;
  };

  // Get effective governance for a feature (considering parent cascade)
  const getEffectiveGovernance = (feature, parentFeature = null) => {
    // If feature has explicit governance, use it
    if (feature.governance_level) {
      return feature.governance_level;
    }
    
    // If it's a tab or sub_page and parent has governance, inherit from parent
    if ((feature.granularity === 'tab' || feature.granularity === 'sub_page') && parentFeature && parentFeature.governance_level) {
      return parentFeature.governance_level;
    }
    
    return null; // Unassigned
  };

  // Toggle page expansion
  const togglePageExpansion = (pageName) => {
    setExpandedPages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pageName)) {
        newSet.delete(pageName);
      } else {
        newSet.add(pageName);
      }
      return newSet;
    });
  };

  // Expand all pages
  const expandAll = () => {
    const allPageNames = systemFeatures
      .filter(f => f.granularity === 'page')
      .map(f => f.name);
    setExpandedPages(new Set(allPageNames));
  };

  // Collapse all pages
  const collapseAll = () => {
    setExpandedPages(new Set());
  };


  const handleCreateFeature = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/custom-features`,
        {
          name: newFeature.name,
          display_name: newFeature.display_name,
          description: newFeature.description,
          base_price: newFeature.base_price || 0,
          price_per_unit: newFeature.price_per_unit || 0,
          pricing_type: newFeature.pricing_type || 'one_time',
          billing_cycle: newFeature.billing_cycle || 'one_time',
          minimum_quantity: newFeature.minimum_quantity || 1,
          maximum_quantity: newFeature.maximum_quantity || -1,
          available_tiers: newFeature.available_tiers || ['starter', 'professional', 'business', 'enterprise']
        }
      );
      
      toast.success(`✅ Custom add-on "${newFeature.display_name}" created successfully!`);
      setShowNewFeatureDialog(false);
      setNewFeature({
        name: '',
        display_name: '',
        description: '',
        base_price: 0,
        price_per_unit: 0,
        pricing_type: 'one_time',
        billing_cycle: 'one_time',
        minimum_quantity: 1,
        maximum_quantity: -1
      });
      fetchData();
    } catch (error) {
      console.error('[CREATE FEATURE] Error:', error);
      console.error('[CREATE FEATURE] Response:', error.response);
      
      if (error.response?.status === 401) {
        toast.error('❌ Authentication failed. Please refresh the page and log in again.');
      } else if (error.response?.status === 403) {
        toast.error('❌ Admin access required to create custom features.');
      } else {
        toast.error('Failed to create feature: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleEditFeature = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/custom-features/${editingFeature.id}`,
        {
          name: editingFeature.name,
          display_name: editingFeature.display_name,
          description: editingFeature.description,
          base_price: editingFeature.base_price || 0,
          price_per_unit: editingFeature.price_per_unit || 0,
          pricing_type: editingFeature.pricing_type || 'one_time',
          billing_cycle: editingFeature.billing_cycle || 'one_time',
          minimum_quantity: editingFeature.minimum_quantity || 1,
          maximum_quantity: editingFeature.maximum_quantity || -1,
          available_tiers: editingFeature.available_tiers || ['starter', 'professional', 'business', 'enterprise']
        }
      );
      
      toast.success(`✅ Custom add-on "${editingFeature.display_name}" updated successfully!`);
      setShowEditFeatureDialog(false);
      setEditingFeature(null);
      fetchData();
    } catch (error) {
      console.error('[EDIT FEATURE] Error:', error);
      
      if (error.response?.status === 401) {
        toast.error('❌ Authentication failed. Please refresh the page and log in again.');
      } else if (error.response?.status === 403) {
        toast.error('❌ Admin access required to edit custom features.');
      } else if (error.response?.status === 404) {
        toast.error('❌ Custom feature not found.');
      } else {
        toast.error('Failed to update feature: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleRemoveFeatureFromTier = async (tierName, featureName) => {
    try {
      const tier = tiers.find(t => t.name === tierName);
      const updatedFeatures = tier.features.filter(f => f !== featureName);
      
      const token = getAuthToken();
      await axios.put(
        `${BACKEND_URL}/api/admin/subscription-tiers/${tierName}`, 
        {
          ...tier,
          features: updatedFeatures
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Removed ${formatFeatureName(featureName)} from ${tier.display_name}`);
      fetchData();
    } catch (error) {
      console.error('Error removing feature:', error);
      
      // Phase 3: Show detailed governance violation errors
      if (error.response?.status === 400 && error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (detail.violations && detail.violations.length > 0) {
          const violationMsg = detail.violations.map(v => 
            `• ${v.feature} requires ${v.governance_level}`
          ).join('\n');
          toast.error(`❌ Governance Violation:\n${violationMsg}\n\nTip: ${detail.help || 'Check Feature Governance settings'}`);
        } else {
          toast.error(detail.message || detail.error || 'Failed to remove feature');
        }
      } else {
        toast.error('Failed to remove feature from tier');
      }
    }
  };

  const handleAddFeatureToTier = async (tierName, featureName) => {
    try {
      // Check governance before adding
      if (!validateFeatureAssignment(featureName, tierName)) {
        const govLevel = getGovernanceLevel(featureName);
        toast.error(`❌ Cannot add ${formatFeatureName(featureName)} to ${tierName} tier. This feature requires ${govLevel.level.replace('_', ' ')}.`);
        return;
      }
      
      const tier = tiers.find(t => t.name === tierName);
      const updatedFeatures = [...new Set([...tier.features, featureName])]; // Remove duplicates
      
      const token = getAuthToken();
      await axios.put(
        `${BACKEND_URL}/api/admin/subscription-tiers/${tierName}`, 
        {
          ...tier,
          features: updatedFeatures
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`✅ Added ${formatFeatureName(featureName)} to ${tier.display_name}`);
      fetchData();
    } catch (error) {
      console.error('Error adding feature:', error);
      
      // Phase 3: Show detailed governance violation errors
      if (error.response?.status === 400 && error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (detail.violations && detail.violations.length > 0) {
          const violationMsg = detail.violations.map(v => 
            `• ${v.feature} requires ${v.governance_level}`
          ).join('\n');
          toast.error(`❌ Governance Violation:\n${violationMsg}\n\nTip: ${detail.help || 'Check Feature Governance settings'}`);
        } else {
          toast.error(detail.message || detail.error || 'Failed to add feature');
        }
      } else {
        toast.error('Failed to add feature to tier');
      }
    }
  };

  const handleDeleteFeature = async (featureName) => {
    try {
      // Find the feature by name (assuming feature_flags collection has features)
      await axios.delete(`${BACKEND_URL}/api/admin/features/${featureName}`);
      
      toast.success(`Feature "${formatFeatureName(featureName)}" deleted successfully`);
      setDeleteConfirmation({ isOpen: false, featureName: null });
      fetchData();
    } catch (error) {
      console.error('Error deleting feature:', error);
      toast.error('Failed to delete feature: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteTier = async (tierName, force = false) => {
    try {
      // If not force and error mentions subscriptions, ask for confirmation
      try {
        await axios.delete(
          `${BACKEND_URL}/api/admin/subscription-tiers/${tierName}${force ? '?force=true' : ''}`,
          { headers: { Authorization: `Bearer ${getAuthToken()}` } }
        );
        
        toast.success(`Subscription tier "${deleteTierConfirmation.tierDisplayName}" deleted successfully`);
        setDeleteTierConfirmation({ isOpen: false, tierName: null, tierDisplayName: null });
        fetchData();
      } catch (firstError) {
        // If error mentions active subscriptions and we haven't forced yet
        if (firstError.response?.status === 400 && !force && firstError.response?.data?.detail?.includes('active subscription')) {
          // Ask for confirmation to force delete
          if (window.confirm(
            `${firstError.response.data.detail}\n\nDo you want to CANCEL all subscriptions and delete this tier?`
          )) {
            // Retry with force=true
            await handleDeleteTier(tierName, true);
          }
        } else {
          throw firstError;
        }
      }
    } catch (error) {
      console.error('Error deleting tier:', error);
      toast.error('Failed to delete tier: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRemoveFromGovernance = async (featureName, governanceLevel) => {
    try {
      const updatedGovernance = { ...governance };
      if (updatedGovernance.feature_restrictions && updatedGovernance.feature_restrictions[governanceLevel]) {
        updatedGovernance.feature_restrictions[governanceLevel] = 
          updatedGovernance.feature_restrictions[governanceLevel].filter(f => f !== featureName);
        
        await axios.put(`${BACKEND_URL}/api/admin/feature-governance`, updatedGovernance);
        toast.success(`Removed ${formatFeatureName(featureName)} from ${governanceLevel.replace('_', ' ')}`);
        fetchData();
      }
    } catch (error) {
      console.error('Error removing from governance:', error);
      toast.error('Failed to remove feature from governance');
    }
  };

  const handleRescanFeatures = async () => {
    try {
      setIsRescanning(true);
      const token = getAuthToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${BACKEND_URL}/api/admin/features/rescan`, {}, { headers });
      
      toast.success(`✅ Rescan complete! Found ${response.data.new_features_count} new features`);
      
      if (response.data.new_features_count > 0) {
        toast.info(`New features detected: ${response.data.new_features.slice(0, 3).join(', ')}${response.data.new_features.length > 3 ? '...' : ''}`);
      }
      
      // Show cleanup results if any
      if (response.data.cleanup_results && response.data.cleanup_results.tiers_cleaned > 0) {
        toast.info(`🧹 Auto-cleanup: Removed ${response.data.cleanup_results.features_removed_from_tiers.length} deprecated features from tiers`);
      }
      
      // Refresh data to show new features
      await fetchData();
    } catch (error) {
      console.error('Error rescanning features:', error);
      toast.error('Failed to rescan features: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsRescanning(false);
    }
  };

  const handleCreateTier = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${BACKEND_URL}/api/admin/subscription-tiers`, newTierForm);
      toast.success('Subscription tier created successfully!');
      setShowCreateTierDialog(false);
      setNewTierForm({
        name: '',
        display_name: '',
        description: '',
        price_monthly: 0,
        price_annual: 0,
        trial_enabled: false,
        trial_days: 0,
        features: []
      });
      fetchData();
    } catch (error) {
      console.error('Error creating tier:', error);
      toast.error('Failed to create tier: ' + (error.response?.data?.detail || error.message));
    }
  };

  const validateFeatureAssignment = (featureName, tierName) => {
    const govLevel = getGovernanceLevel(featureName);
    
    if (govLevel.level === 'enterprise_only' && tierName !== 'enterprise') {
      return false;
    }
    if (govLevel.level === 'business_minimum' && !['business', 'enterprise'].includes(tierName)) {
      return false;
    }
    if (govLevel.level === 'professional_minimum' && !['professional', 'business', 'enterprise'].includes(tierName)) {
      return false;
    }
    
    return true;
  };

  const getViolations = () => {
    const violations = [];
    
    tiers.forEach(tier => {
      tier.features?.forEach(featureName => {
        if (!validateFeatureAssignment(featureName, tier.name)) {
          violations.push({
            tier: tier.display_name,
            tierName: tier.name,
            feature: featureName,
            requiredLevel: getGovernanceLevel(featureName).level
          });
        }
      });
    });
    
    return violations;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const violations = getViolations();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            Feature Governance System
          </h1>
          <p className="text-gray-600 mt-1">
            Manage feature restrictions and subscription tier rules
          </p>
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowCreateTierDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Subscription Tier
          </Button>
          
          <Dialog open={showNewFeatureDialog} onOpenChange={setShowNewFeatureDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Custom Add-on
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Custom Add-on Feature</DialogTitle>
                <DialogDescription>
                  Create a custom purchasable add-on with pricing (e.g., Premium Support, Training Package)
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateFeature} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="feature_name">Feature ID</Label>
                    <Input
                      id="feature_name"
                      value={newFeature.name}
                      onChange={(e) => setNewFeature(prev => ({...prev, name: e.target.value}))}
                      placeholder="e.g., premium_support"
                      required
                    />
                    <p className="text-xs text-gray-500">Lowercase, use underscores</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="display_name">Display Name</Label>
                    <Input
                      id="display_name"
                      value={newFeature.display_name}
                      onChange={(e) => setNewFeature(prev => ({...prev, display_name: e.target.value}))}
                      placeholder="e.g., Premium Support"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newFeature.description}
                    onChange={(e) => setNewFeature(prev => ({...prev, description: e.target.value}))}
                    placeholder="What does this add-on provide?"
                    rows={2}
                  />
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Pricing</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="base_price">Base Price ($)</Label>
                      <Input
                        id="base_price"
                        type="number"
                        step="0.01"
                        value={newFeature.base_price || 0}
                        onChange={(e) => setNewFeature(prev => ({...prev, base_price: parseFloat(e.target.value) || 0}))}
                        placeholder="0.00"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="price_per_unit">Price Per Unit ($)</Label>
                      <Input
                        id="price_per_unit"
                        type="number"
                        step="0.01"
                        value={newFeature.price_per_unit || 0}
                        onChange={(e) => setNewFeature(prev => ({...prev, price_per_unit: parseFloat(e.target.value) || 0}))}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="pricing_type">Pricing Type</Label>
                      <Select 
                        value={newFeature.pricing_type || "one_time"} 
                        onValueChange={(value) => setNewFeature(prev => ({...prev, pricing_type: value}))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="one_time">One-time</SelectItem>
                          <SelectItem value="recurring">Recurring</SelectItem>
                          <SelectItem value="usage_based">Usage Based</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="billing_cycle">Billing Cycle</Label>
                      <Select 
                        value={newFeature.billing_cycle || "one_time"} 
                        onValueChange={(value) => setNewFeature(prev => ({...prev, billing_cycle: value}))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="one_time">One-time</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="annual">Annual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Quantity Limits</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="minimum_quantity">Minimum Quantity</Label>
                      <Input
                        id="minimum_quantity"
                        type="number"
                        value={newFeature.minimum_quantity || 1}
                        onChange={(e) => setNewFeature(prev => ({...prev, minimum_quantity: parseInt(e.target.value) || 1}))}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="maximum_quantity">Maximum Quantity</Label>
                      <Input
                        id="maximum_quantity"
                        type="number"
                        value={newFeature.maximum_quantity || -1}
                        onChange={(e) => setNewFeature(prev => ({...prev, maximum_quantity: parseInt(e.target.value) || -1}))}
                        placeholder="-1 for unlimited"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowNewFeatureDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-purple-600 hover:bg-purple-700">Create Add-on</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Custom Feature Dialog */}
          <Dialog open={showEditFeatureDialog} onOpenChange={setShowEditFeatureDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Custom Add-on Feature</DialogTitle>
                <DialogDescription>
                  Update pricing and details for this custom add-on
                </DialogDescription>
              </DialogHeader>
              {editingFeature && (
                <form onSubmit={handleEditFeature} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit_feature_name">Feature ID</Label>
                      <Input
                        id="edit_feature_name"
                        value={editingFeature.name}
                        onChange={(e) => setEditingFeature(prev => ({...prev, name: e.target.value}))}
                        placeholder="e.g., premium_support"
                        required
                      />
                      <p className="text-xs text-gray-500">Lowercase, use underscores</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="edit_display_name">Display Name</Label>
                      <Input
                        id="edit_display_name"
                        value={editingFeature.display_name}
                        onChange={(e) => setEditingFeature(prev => ({...prev, display_name: e.target.value}))}
                        placeholder="e.g., Premium Support"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="edit_description">Description</Label>
                    <Textarea
                      id="edit_description"
                      value={editingFeature.description || ''}
                      onChange={(e) => setEditingFeature(prev => ({...prev, description: e.target.value}))}
                      placeholder="What does this add-on provide?"
                      rows={2}
                    />
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Pricing</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit_base_price">Base Price ($)</Label>
                        <Input
                          id="edit_base_price"
                          type="number"
                          step="0.01"
                          value={editingFeature.base_price || 0}
                          onChange={(e) => setEditingFeature(prev => ({...prev, base_price: parseFloat(e.target.value) || 0}))}
                          placeholder="0.00"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="edit_price_per_unit">Price Per Unit ($)</Label>
                        <Input
                          id="edit_price_per_unit"
                          type="number"
                          step="0.01"
                          value={editingFeature.price_per_unit || 0}
                          onChange={(e) => setEditingFeature(prev => ({...prev, price_per_unit: parseFloat(e.target.value) || 0}))}
                          placeholder="0.00"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="edit_pricing_type">Pricing Type</Label>
                        <Select 
                          value={editingFeature.pricing_type || "one_time"} 
                          onValueChange={(value) => setEditingFeature(prev => ({...prev, pricing_type: value}))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="one_time">One-time</SelectItem>
                            <SelectItem value="recurring">Recurring</SelectItem>
                            <SelectItem value="usage_based">Usage Based</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="edit_billing_cycle">Billing Cycle</Label>
                        <Select 
                          value={editingFeature.billing_cycle || "one_time"} 
                          onValueChange={(value) => setEditingFeature(prev => ({...prev, billing_cycle: value}))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="one_time">One-time</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="annual">Annual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Quantity Limits</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit_minimum_quantity">Minimum Quantity</Label>
                        <Input
                          id="edit_minimum_quantity"
                          type="number"
                          value={editingFeature.minimum_quantity || 1}
                          onChange={(e) => setEditingFeature(prev => ({...prev, minimum_quantity: parseInt(e.target.value) || 1}))}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="edit_maximum_quantity">Maximum Quantity</Label>
                        <Input
                          id="edit_maximum_quantity"
                          type="number"
                          value={editingFeature.maximum_quantity || -1}
                          onChange={(e) => setEditingFeature(prev => ({...prev, maximum_quantity: parseInt(e.target.value) || -1}))}
                          placeholder="-1 for unlimited"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => {
                      setShowEditFeatureDialog(false);
                      setEditingFeature(null);
                    }}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-purple-600 hover:bg-purple-700">Save Changes</Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Violations Alert */}
      {violations.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-5 w-5" />
              Governance Violations Detected
            </CardTitle>
            <CardDescription className="text-red-700">
              {violations.length} feature(s) are assigned to tiers that violate governance rules
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {violations.map((violation, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-white rounded border border-red-200">
                  <div>
                    <span className="font-medium text-red-900">{formatFeatureName(violation.feature)}</span>
                    <span className="text-red-700 text-sm ml-2">
                      in {violation.tier} (requires {violation.requiredLevel.replace('_', ' ')})
                    </span>
                  </div>
                  <XCircle className="h-4 w-4 text-red-500" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="tiers" className="w-full">
        <TabsList>
          <TabsTrigger value="tiers">Subscription Tiers</TabsTrigger>
          <TabsTrigger value="governance">Governance Rules</TabsTrigger>
          <TabsTrigger value="system-features">System Features</TabsTrigger>
          <TabsTrigger value="system-addons">System Addons</TabsTrigger>
          <TabsTrigger value="custom-features">Custom Features</TabsTrigger>
        </TabsList>

        {/* Subscription Plans Tab - Rebuilt from scratch */}
        <TabsContent value="tiers" className="space-y-4">
          {/* Info Banner */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">Subscription Tiers - Read Only</h3>
            <p className="text-sm text-blue-800">
              This tab displays subscription tiers and their features. <strong>Features are automatically assigned</strong> when you set governance in the <strong>System Features</strong> tab. 
              Pricing and usage limits are managed in your database configuration. You can only <strong>Delete</strong> tiers here.
            </p>
          </div>

          <div className="grid gap-6">
            {tiers.map((tier) => (
              <Card key={tier.name} className={`border-2 ${getTierColor(tier.name)}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      {getTierIcon(tier.name)}
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {tier.display_name}
                          <Badge variant="outline" className={getTierColor(tier.name)}>
                            {tier.name.toUpperCase()}
                          </Badge>
                        </CardTitle>
                        <CardDescription>{tier.description}</CardDescription>
                      </div>
                    </div>
                    {tier.name !== 'admin' && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeleteTierConfirmation({
                          isOpen: true,
                          tierName: tier.name,
                          tierDisplayName: tier.display_name
                        })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Features - READ ONLY from Governance */}
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Shield className="h-4 w-4 text-gray-600" />
                      Features ({tier.features?.length || 0})
                      <Badge variant="outline" className="bg-gray-100 text-gray-700 text-xs">READ-ONLY</Badge>
                    </h4>
                    <p className="text-xs text-gray-600 mb-3">
                      Features automatically assigned from Feature Governance. Go to <strong>System Features</strong> tab to assign features to tiers.
                    </p>
                    {tier.features?.length === 0 ? (
                      <div className="border rounded-lg p-6 bg-gray-50 text-center">
                        <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600 font-medium">No features assigned yet</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Set governance in System Features tab to assign features to this tier
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1 max-h-64 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                        {tier.features.map((featureName) => (
                          <div key={featureName} className="flex items-center gap-2 text-sm py-1">
                            <CheckCircle className="h-3 w-3 text-green-600 flex-shrink-0" />
                            <span className="text-gray-700">{getFeatureDisplayName(featureName)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Governance Rules Tab */}
        <TabsContent value="governance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Feature Governance Rules</CardTitle>
              <CardDescription>
                These rules prevent features from being assigned to inappropriate tiers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Professional Minimum */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Zap className="h-4 w-4 text-blue-600" />
                    Professional Tier Minimum
                  </h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Features that require at least Professional tier (Professional, Business, Enterprise)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {governance?.feature_restrictions?.professional_minimum?.map((feature) => (
                      <Badge key={feature} variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
                        {formatFeatureName(feature)}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Business Minimum */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-green-600" />
                    Business Tier Minimum
                  </h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Features that require at least Business tier (Business, Enterprise)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {governance?.feature_restrictions?.business_minimum?.map((feature) => (
                      <Badge key={feature} variant="outline" className="bg-green-50 text-green-800 border-green-200">
                        {formatFeatureName(feature)}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Enterprise Only */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Crown className="h-4 w-4 text-purple-600" />
                    Enterprise Tier Only
                  </h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Features that are exclusively available in Enterprise tier
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {governance?.feature_restrictions?.enterprise_only?.map((feature) => (
                      <Badge key={feature} variant="outline" className="bg-purple-50 text-purple-800 border-purple-200">
                        {formatFeatureName(feature)}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Add-on Overrides - NEW PHASE 3 */}
                <div className="border-t pt-6 mt-6">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Plus className="h-4 w-4 text-orange-600" />
                    Add-on Override Rules
                    <Badge variant="outline" className="ml-2 bg-orange-50 text-orange-600">Phase 3</Badge>
                  </h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Features that can be unlocked via add-on purchases (bypassing tier requirements)
                  </p>
                  
                  {governance?.addon_overrides && Object.keys(governance.addon_overrides).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(governance.addon_overrides).map(([featureName, config]) => (
                        <div key={featureName} className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-orange-900">
                                {formatFeatureName(featureName)}
                              </p>
                              <p className="text-sm text-orange-700 mt-1">
                                {config.allowed_via_addon ? (
                                  <>
                                    ✅ Available via add-on: <span className="font-semibold">{config.addon_id}</span>
                                    {config.min_tier_required && (
                                      <> • Minimum tier: <span className="font-semibold">{config.min_tier_required}</span></>
                                    )}
                                  </>
                                ) : (
                                  "❌ Not available via add-on"
                                )}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                // TODO: Edit add-on override
                                toast.info("Edit add-on override coming soon");
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <p className="text-gray-600 mb-3">No add-on overrides configured</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          toast.info("Add-on override management coming in next update");
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Override Rule
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Features Tab */}
        <TabsContent value="system-features" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-blue-600" />
                    System Features (Auto-detected)
                  </CardTitle>
                  <CardDescription>
                    Features automatically detected from your codebase - {systemFeatures.length} features found
                    {systemFeatures.length > 0 && (() => {
                      const pages = systemFeatures.filter(f => f.granularity === 'page').length;
                      const tabs = systemFeatures.filter(f => f.granularity === 'tab').length;
                      const subPages = systemFeatures.filter(f => f.granularity === 'sub_page').length;
                      return ` (${pages} pages + ${tabs} tabs + ${subPages} sub-pages)`;
                    })()}
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRescanFeatures}
                  disabled={isRescanning}
                  className="ml-2"
                >
                  {isRescanning ? (
                    <>
                      <div className="animate-spin h-4 w-4 mr-2 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Rescan Features
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search and Pagination Controls */}
              {systemFeatures.length > 0 && (
                <>
                  <div className="mb-4 flex items-center justify-between gap-4">
                    {/* Search */}
                    <div className="flex-1 max-w-md">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search features..."
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1); // Reset to first page on search
                          }}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {searchQuery && (
                          <button
                            onClick={() => {
                              setSearchQuery('');
                              setCurrentPage(1);
                            }}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Items per page */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Show:</span>
                      <Select value={itemsPerPage.toString()} onValueChange={(value) => {
                        setItemsPerPage(parseInt(value));
                        setCurrentPage(1);
                      }}>
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Governance Filter Buttons - Dynamic based on DB tiers */}
                  <div className="mb-4 flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-gray-600 font-medium">Filter by Governance:</span>
                    {getGovernanceFilterButtons().map(button => (
                      <Button
                        key={button.value}
                        variant={governanceFilter === button.value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          setGovernanceFilter(button.value);
                          setCurrentPage(1);
                        }}
                      >
                        {button.label}
                      </Button>
                    ))}
                  </div>
                </>
              )}
              
              <div className="space-y-3">
                {systemFeatures.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Zap className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No system features detected yet</p>
                    <p className="text-sm">Features will be automatically scanned from your codebase</p>
                  </div>
                ) : (
                  (() => {
                    // Filter features based on search query and governance filter
                    const filteredFeatures = systemFeatures.filter(feature => {
                      // Search filter
                      const matchesSearch = feature.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        feature.display_name.toLowerCase().includes(searchQuery.toLowerCase());
                      
                      if (!matchesSearch) return false;
                      
                      // Governance filter
                      if (governanceFilter === 'all') return true;
                      
                      const featureGovLevel = feature.governance_level;
                      
                      if (governanceFilter === 'unassigned') {
                        // Unassigned = Features with no governance set (null/undefined)
                        // Default state for new system features
                        return !featureGovLevel || featureGovLevel === null || featureGovLevel === undefined;
                      }
                      
                      if (governanceFilter === 'free_minimum') {
                        // Free Minimum = Features explicitly set to "free_minimum" or "none" (backward compat)
                        // These are available in all tiers including Free
                        return featureGovLevel === 'free_minimum' || featureGovLevel === 'none';
                      }
                      
                      // Match specific governance level
                      return featureGovLevel === governanceFilter;
                    });
                    
                    console.log(`[FILTER] Active: ${governanceFilter}, Total: ${systemFeatures.length}, Filtered: ${filteredFeatures.length}`);
                    
                    // Organize into hierarchy (pages with tabs)
                    const hierarchicalFeatures = organizeFeatureHierarchy(filteredFeatures);
                    
                    // Calculate pagination on page level (not individual features)
                    const totalPages = Math.ceil(hierarchicalFeatures.length / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedPages = hierarchicalFeatures.slice(startIndex, endIndex);
                    
                    if (hierarchicalFeatures.length === 0) {
                      return (
                        <div className="text-center py-8 text-gray-500">
                          <Search className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                          <p>No features found matching "{searchQuery}"</p>
                        </div>
                      );
                    }
                    
                    return (
                      <>
                        {/* Show results info and expand/collapse controls */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm text-gray-600">
                            Showing {startIndex + 1}-{Math.min(endIndex, hierarchicalFeatures.length)} of {hierarchicalFeatures.length} pages
                            {searchQuery && ` (filtered from ${systemFeatures.filter(f => f.granularity === 'page').length} total)`}
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={expandAll}>
                              <ChevronDown className="h-4 w-4 mr-1" />
                              Expand All
                            </Button>
                            <Button variant="outline" size="sm" onClick={collapseAll}>
                              <ChevronUp className="h-4 w-4 mr-1" />
                              Collapse All
                            </Button>
                          </div>
                        </div>
                        
                        {/* Hierarchical Features list with Accordion */}
                        <div className="space-y-3">
                          {paginatedPages.map((page) => {
                            const isExpanded = expandedPages.has(page.name);
                            const pageHasTabs = page.children && page.children.length > 0;
                            
                            return (
                              <div key={page.name} className="border rounded-lg overflow-hidden">
                                {/* Page Header - Always Visible */}
                                <div className="bg-white border-b">
                                  <div className="p-4">
                                    <div className="flex items-center justify-between">
                                      {/* Left: Page Info */}
                                      <div className="flex items-center gap-3 flex-1">
                                        {/* Expand/Collapse Button */}
                                        {pageHasTabs && (
                                          <button
                                            onClick={() => togglePageExpansion(page.name)}
                                            className="p-1 hover:bg-gray-100 rounded"
                                          >
                                            {isExpanded ? (
                                              <ChevronDown className="h-5 w-5 text-gray-600" />
                                            ) : (
                                              <ChevronRight className="h-5 w-5 text-gray-600" />
                                            )}
                                          </button>
                                        )}
                                        {!pageHasTabs && <div className="w-7" />}
                                        
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="font-semibold text-base">{page.display_name}</span>
                                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-xs">
                                            PAGE
                                          </Badge>
                                          {pageHasTabs && (() => {
                                            const tabs = page.children.filter(c => c.granularity === 'tab').length;
                                            const subPages = page.children.filter(c => c.granularity === 'sub_page').length;
                                            const parts = [];
                                            if (tabs > 0) parts.push(`${tabs} ${tabs === 1 ? 'Tab' : 'Tabs'}`);
                                            if (subPages > 0) parts.push(`${subPages} ${subPages === 1 ? 'Sub-page' : 'Sub-pages'}`);
                                            return (
                                              <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-300 text-xs">
                                                {parts.join(' + ')}
                                              </Badge>
                                            );
                                          })()}
                                          {(() => {
                                            const govLevel = page.governance_level;
                                            
                                            if (govLevel === 'enterprise_only') {
                                              return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300 text-xs">ENTERPRISE ONLY</Badge>;
                                            } else if (govLevel === 'business_minimum') {
                                              return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">BUSINESS MINIMUM</Badge>;
                                            } else if (govLevel === 'professional_minimum') {
                                              return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-xs">PROFESSIONAL MINIMUM</Badge>;
                                            } else if (govLevel === 'starter_minimum') {
                                              return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-300 text-xs">STARTER MINIMUM</Badge>;
                                            } else {
                                              return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 text-xs">UNASSIGNED</Badge>;
                                            }
                                          })()}
                                        </div>
                                      </div>
                                      
                                      {/* Right: Page Governance Control */}
                                      <div className="flex items-center gap-2">
                                        <Select 
                                          value={page.governance_level || ""} 
                                          onValueChange={async (governanceLevel) => {
                                            if (governanceLevel) {
                                              try {
                                                const token = getAuthToken();
                                                
                                                // Handle "unassign" option differently
                                                if (governanceLevel === 'unassign') {
                                                  const response = await axios.post(
                                                    `${BACKEND_URL}/api/admin/features/unassign`,
                                                    {
                                                      features: [page.name],
                                                      remove_from_tiers: true
                                                    },
                                                    { headers: { Authorization: `Bearer ${token}` } }
                                                  );
                                                  
                                                  toast.success(`Unassigned ${page.display_name} from all tiers`);
                                                } else {
                                                  const response = await axios.post(
                                                    `${BACKEND_URL}/api/admin/feature-governance/set-level`,
                                                    {
                                                      feature_name: page.name,
                                                      governance_level: governanceLevel
                                                    },
                                                    { headers: { Authorization: `Bearer ${token}` } }
                                                  );
                                                  
                                                  toast.success(`Set ${page.display_name} to ${governanceLevel.replace('_', ' ')}`);
                                                }
                                                fetchData();
                                              } catch (error) {
                                                console.error('Error setting governance:', error);
                                                toast.error('Failed to set governance level');
                                              }
                                            }
                                          }}
                                        >
                                          <SelectTrigger className="w-52">
                                            <SelectValue placeholder="Set Governance Level" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {getGovernanceOptions().map(option => (
                                              <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Children (Tabs and Sub-pages) - Collapsible */}
                                {pageHasTabs && isExpanded && (
                                  <div className="bg-gray-50 p-4 space-y-2">
                                    {page.children.map((child) => {
                                      const effectiveGov = getEffectiveGovernance(child, page);
                                      const isInherited = !child.governance_level && page.governance_level;
                                      const isSubPage = child.granularity === 'sub_page';
                                      const hasSubTabs = child.has_sub_tabs || (child.children && child.children.length > 0);
                                      const isTabExpanded = expandedTabs.has(child.name);
                                      
                                      // Toggle function for tab expansion
                                      const toggleTabExpansion = () => {
                                        setExpandedTabs(prev => {
                                          const newSet = new Set(prev);
                                          if (newSet.has(child.name)) {
                                            newSet.delete(child.name);
                                          } else {
                                            newSet.add(child.name);
                                          }
                                          return newSet;
                                        });
                                      };
                                      
                                      return (
                                        <div key={child.name} className="space-y-2">
                                          <div className="bg-white p-3 rounded border border-gray-200 ml-8">
                                            <div className="flex items-center justify-between">
                                              {/* Left: Child Info */}
                                              <div className="flex items-center gap-2 flex-wrap">
                                                {/* Expand/Collapse button for tabs with sub-tabs */}
                                                {hasSubTabs && (
                                                  <button
                                                    onClick={toggleTabExpansion}
                                                    className="p-1 hover:bg-gray-100 rounded"
                                                    title={isTabExpanded ? "Collapse sub-tabs" : "Expand sub-tabs"}
                                                  >
                                                    {isTabExpanded ? (
                                                      <ChevronDown className="h-4 w-4 text-gray-500" />
                                                    ) : (
                                                      <ChevronRight className="h-4 w-4 text-gray-500" />
                                                    )}
                                                  </button>
                                                )}
                                                <span className="text-sm font-medium">{child.display_name}</span>
                                                <Badge variant="outline" className={isSubPage ? "bg-cyan-50 text-cyan-700 border-cyan-300 text-xs" : "bg-indigo-50 text-indigo-700 border-indigo-300 text-xs"}>
                                                  {isSubPage ? 'SUB-PAGE' : 'TAB'}
                                                </Badge>
                                                {hasSubTabs && (
                                                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs">
                                                    {child.sub_tabs_count || (child.children ? child.children.length : 0)} Sub-tabs
                                                  </Badge>
                                                )}
                                                {isSubPage && child.route && (
                                                  <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300 text-xs font-mono">
                                                    {child.route}
                                                  </Badge>
                                                )}
                                                {isInherited && (
                                                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300 text-xs">
                                                    Inherited from Page
                                                  </Badge>
                                                )}
                                                {(() => {
                                                  if (effectiveGov === 'enterprise_only') {
                                                    return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300 text-xs">ENTERPRISE ONLY</Badge>;
                                                  } else if (effectiveGov === 'business_minimum') {
                                                    return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">BUSINESS MINIMUM</Badge>;
                                                  } else if (effectiveGov === 'professional_minimum') {
                                                    return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-xs">PROFESSIONAL MINIMUM</Badge>;
                                                  } else if (effectiveGov === 'starter_minimum') {
                                                    return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-300 text-xs">STARTER MINIMUM</Badge>;
                                                  } else {
                                                    return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 text-xs">UNASSIGNED</Badge>;
                                                  }
                                                })()}
                                              </div>
                                              
                                              {/* Right: Child Governance Control */}
                                              <div className="flex items-center gap-2">
                                                <Select 
                                                  value={child.governance_level || ""} 
                                                  onValueChange={async (governanceLevel) => {
                                                    if (governanceLevel) {
                                                      try {
                                                        const token = getAuthToken();
                                                        
                                                        // Handle "unassign" option differently
                                                        if (governanceLevel === 'unassign') {
                                                          const response = await axios.post(
                                                            `${BACKEND_URL}/api/admin/features/unassign`,
                                                            {
                                                              features: [child.name],
                                                              remove_from_tiers: true
                                                            },
                                                            { headers: { Authorization: `Bearer ${token}` } }
                                                          );
                                                          
                                                          toast.success(`Unassigned ${child.display_name} from all tiers`);
                                                        } else {
                                                          const response = await axios.post(
                                                            `${BACKEND_URL}/api/admin/feature-governance/set-level`,
                                                            {
                                                              feature_name: child.name,
                                                              governance_level: governanceLevel
                                                            },
                                                            { headers: { Authorization: `Bearer ${token}` } }
                                                          );
                                                          
                                                          toast.success(`Set ${child.display_name} to ${governanceLevel.replace('_', ' ')}`);
                                                        }
                                                        fetchData();
                                                      } catch (error) {
                                                        console.error('Error setting governance:', error);
                                                        toast.error('Failed to set governance level');
                                                      }
                                                    }
                                                  }}
                                                >
                                                  <SelectTrigger className="w-48 text-xs">
                                                    <SelectValue placeholder={isInherited ? "Inheriting from Page" : "Override Governance"} />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    {getGovernanceOptions().map(option => (
                                                      <SelectItem key={option.value} value={option.value}>
                                                        {option.label}
                                                      </SelectItem>
                                                    ))}
                                                  </SelectContent>
                                                </Select>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          {/* Sub-tabs nested under the parent tab */}
                                          {hasSubTabs && isTabExpanded && child.children && child.children.length > 0 && (
                                            <div className="ml-16 space-y-1">
                                              {child.children.map((subTab) => {
                                                const subTabEffectiveGov = subTab.governance_level || child.governance_level || page.governance_level || 'unassigned';
                                                const subTabIsInherited = !subTab.governance_level && (child.governance_level || page.governance_level);
                                                
                                                return (
                                                  <div key={subTab.name} className="bg-white p-2 rounded border border-gray-200 border-dashed">
                                                    <div className="flex items-center justify-between">
                                                      <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="text-xs font-medium text-gray-600">{subTab.display_name}</span>
                                                        <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-300 text-[10px]">
                                                          SUB-TAB
                                                        </Badge>
                                                        {subTabIsInherited && (
                                                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300 text-[10px]">
                                                            Inherited
                                                          </Badge>
                                                        )}
                                                        {(() => {
                                                          if (subTabEffectiveGov === 'enterprise_only') {
                                                            return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300 text-[10px]">ENTERPRISE</Badge>;
                                                          } else if (subTabEffectiveGov === 'business_minimum') {
                                                            return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-[10px]">BUSINESS</Badge>;
                                                          } else if (subTabEffectiveGov === 'professional_minimum') {
                                                            return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-[10px]">PROFESSIONAL</Badge>;
                                                          } else if (subTabEffectiveGov === 'starter_minimum') {
                                                            return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-300 text-[10px]">STARTER</Badge>;
                                                          } else {
                                                            return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 text-[10px]">UNASSIGNED</Badge>;
                                                          }
                                                        })()}
                                                      </div>
                                                      
                                                      <Select 
                                                        value={subTab.governance_level || ""} 
                                                        onValueChange={async (governanceLevel) => {
                                                          if (governanceLevel) {
                                                            try {
                                                              const token = getAuthToken();
                                                              if (governanceLevel === 'unassign') {
                                                                await axios.post(
                                                                  `${BACKEND_URL}/api/admin/features/unassign`,
                                                                  { features: [subTab.name], remove_from_tiers: true },
                                                                  { headers: { Authorization: `Bearer ${token}` } }
                                                                );
                                                                toast.success(`Unassigned ${subTab.display_name}`);
                                                              } else {
                                                                await axios.post(
                                                                  `${BACKEND_URL}/api/admin/feature-governance/set-level`,
                                                                  { feature_name: subTab.name, governance_level: governanceLevel },
                                                                  { headers: { Authorization: `Bearer ${token}` } }
                                                                );
                                                                toast.success(`Set ${subTab.display_name} to ${governanceLevel.replace('_', ' ')}`);
                                                              }
                                                              fetchData();
                                                            } catch (error) {
                                                              console.error('Error setting governance:', error);
                                                              toast.error('Failed to set governance level');
                                                            }
                                                          }
                                                        }}
                                                      >
                                                        <SelectTrigger className="w-40 text-[10px] h-7">
                                                          <SelectValue placeholder="Override" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                          {getGovernanceOptions().map(option => (
                                                            <SelectItem key={option.value} value={option.value} className="text-xs">
                                                              {option.label}
                                                            </SelectItem>
                                                          ))}
                                                        </SelectContent>
                                                      </Select>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                          <div className="flex items-center justify-between mt-6 pt-4 border-t">
                            <div className="text-sm text-gray-600">
                              Page {currentPage} of {totalPages}
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {/* Previous Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                              >
                                <ChevronLeft className="h-4 w-4 mr-1" />
                                Previous
                              </Button>
                              
                              {/* Page Numbers */}
                              <div className="flex items-center gap-1">
                                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                  let pageNum;
                                  if (totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                  } else {
                                    pageNum = currentPage - 2 + i;
                                  }
                                  
                                  return (
                                    <Button
                                      key={pageNum}
                                      variant={currentPage === pageNum ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => setCurrentPage(pageNum)}
                                      className="w-10 h-10"
                                    >
                                      {pageNum}
                                    </Button>
                                  );
                                })}
                              </div>
                              
                              {/* Next Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                              >
                                Next
                                <ChevronRight className="h-4 w-4 ml-1" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Features Tab - Similar to System Addons */}
        <TabsContent value="custom-features" className="space-y-4">
          {/* Info Banner */}
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <h3 className="font-semibold text-purple-900 mb-2">⚙️ Custom Add-on Features</h3>
            <p className="text-sm text-purple-800">
              Create custom add-on features that users can purchase - like premium support, training packages, 
              consulting hours, or any other service. Define pricing and quantities for each custom add-on.
            </p>
          </div>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5 text-purple-600" />
                    Custom Add-on Features
                  </CardTitle>
                  <CardDescription>
                    Custom purchasable features - {customFeatures.length} total
                  </CardDescription>
                </div>
                <Button onClick={() => setShowNewFeatureDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Custom Add-on
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {customFeatures.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Settings className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No custom add-on features yet</p>
                    <p className="text-sm">Create custom add-ons like Premium Support ($99/mo), Training Package ($500), etc.</p>
                  </div>
                ) : (
                  customFeatures.map((feature) => (
                    <div key={feature.id} className="p-4 border rounded-lg border-gray-200 hover:border-purple-300 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold text-lg">{feature.display_name}</span>
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300 text-xs">
                              CUSTOM
                            </Badge>
                          </div>
                          
                          {feature.description && (
                            <p className="text-sm text-gray-600 mb-3">{feature.description}</p>
                          )}

                          {/* Pricing Section - TODO: Add these fields to custom features */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                            <div className="p-3 border rounded bg-purple-50">
                              <div className="text-xs text-gray-600 mb-1">Base Price</div>
                              <div className="text-xl font-bold text-purple-700">
                                ${feature.base_price || 0}
                              </div>
                            </div>
                            <div className="p-3 border rounded bg-purple-50">
                              <div className="text-xs text-gray-600 mb-1">Per Unit</div>
                              <div className="text-xl font-bold text-purple-700">
                                ${feature.price_per_unit || 0}
                              </div>
                            </div>
                            <div className="p-3 border rounded bg-gray-50">
                              <div className="text-xs text-gray-600 mb-1">Type</div>
                              <div className="text-sm font-semibold capitalize">
                                {feature.pricing_type || 'one_time'}
                              </div>
                            </div>
                            <div className="p-3 border rounded bg-gray-50">
                              <div className="text-xs text-gray-600 mb-1">Billing</div>
                              <div className="text-sm font-semibold capitalize">
                                {feature.billing_cycle || 'One-time'}
                              </div>
                            </div>
                          </div>

                          {/* Quantity Limits */}
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className="p-3 border rounded bg-orange-50">
                              <div className="text-xs text-gray-600 mb-1">Min Quantity</div>
                              <div className="text-lg font-bold text-orange-700">
                                {feature.minimum_quantity || 1}
                              </div>
                            </div>
                            <div className="p-3 border rounded bg-orange-50">
                              <div className="text-xs text-gray-600 mb-1">Max Quantity</div>
                              <div className="text-lg font-bold text-orange-700">
                                {feature.maximum_quantity === -1 ? '∞' : (feature.maximum_quantity || 'N/A')}
                              </div>
                            </div>
                          </div>

                          {/* Available to Tiers */}
                          {feature.available_tiers && feature.available_tiers.length > 0 && (
                            <div className="mb-2">
                              <span className="text-sm text-gray-600">Available to:</span>
                              <div className="flex gap-2 mt-1">
                                {feature.available_tiers.map((tier) => (
                                  <Badge key={tier} variant="outline" className="text-xs capitalize">
                                    {tier}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Status Info */}
                          <div className="flex gap-2 text-xs text-gray-500">
                            <span>Feature ID: <code className="bg-gray-100 px-1 rounded">{feature.name}</code></span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2 ml-4">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setEditingFeature(feature);
                              setShowEditFeatureDialog(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={async () => {
                              try {
                                const token = getAuthToken();
                                await axios.delete(
                                  `${BACKEND_URL}/api/admin/custom-features/${feature.id}`,
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                toast.success('Custom add-on deleted');
                                fetchData();
                              } catch (error) {
                                console.error('Error deleting custom feature:', error);
                                toast.error('Failed to delete custom add-on');
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Addons Tab - Capacity-Based Add-on Services */}
        <TabsContent value="system-addons" className="space-y-4">
          {/* Info Banner */}
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="font-semibold text-green-900 mb-2">🔢 System Addons - Core Capacity Add-ons</h3>
            <p className="text-sm text-green-800">
              System addons allow users to purchase additional capacity in 4 core areas:
              <strong> Business Plans, Team Members, Scenarios, and Subsidiaries</strong>.
            </p>
            <p className="text-sm text-green-800 mt-2">
              <strong>Pricing & quantities are defined here</strong>, separate from subscription tier pricing.
            </p>
          </div>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-green-600" />
                    Capacity-Based Add-on Services
                  </CardTitle>
                  <CardDescription>
                    {(() => {
                      // Filter for core 4 capacity addons only
                      const coreAddons = systemAddons.filter(addon => {
                        if (!addon.limit_increase) return false;
                        const limitKeys = Object.keys(addon.limit_increase);
                        return limitKeys.some(key => 
                          ['business_plans', 'users', 'scenarios', 'subsidiaries'].includes(key)
                        );
                      });
                      return `${coreAddons.length} core capacity add-ons • ${coreAddons.filter(a => a.is_active).length} active`;
                    })()}
                  </CardDescription>
                </div>
                <Button onClick={() => setShowNewAddonDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Addon
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(() => {
                  // Filter for ONLY the 4 core capacity addons
                  const coreAddons = systemAddons.filter(addon => {
                    if (!addon.limit_increase) return false;
                    const limitKeys = Object.keys(addon.limit_increase);
                    return limitKeys.some(key => 
                      ['business_plans', 'users', 'scenarios', 'subsidiaries'].includes(key)
                    );
                  });

                  if (coreAddons.length === 0) {
                    return (
                      <div className="text-center py-8 text-gray-500">
                        <Building2 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No core capacity add-ons found</p>
                        <p className="text-sm">Only showing: Business Plans, Team Members, Scenarios, and Subsidiaries</p>
                      </div>
                    );
                  }

                  return coreAddons.map((addon) => (
                    <div key={addon.id} className="p-4 border rounded-lg border-gray-200 hover:border-green-300 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold text-lg">{addon.display_name}</span>
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">
                              QUANTIFIABLE
                            </Badge>
                            {addon.is_active ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                ACTIVE
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-300 text-xs">
                                <XCircle className="h-3 w-3 mr-1" />
                                INACTIVE
                              </Badge>
                            )}
                            {addon.is_featured && (
                              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300 text-xs">
                                ⭐ FEATURED
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{addon.description}</p>
                          
                          {/* Pricing Info */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                            <div className="p-3 border rounded bg-blue-50">
                              <div className="text-xs text-gray-600 mb-1">Base Price</div>
                              <div className="text-xl font-bold text-blue-700">
                                ${addon.base_price || 0}
                              </div>
                            </div>
                            <div className="p-3 border rounded bg-blue-50">
                              <div className="text-xs text-gray-600 mb-1">Per Unit</div>
                              <div className="text-xl font-bold text-blue-700">
                                ${addon.price_per_unit || 0}
                              </div>
                            </div>
                            <div className="p-3 border rounded bg-gray-50">
                              <div className="text-xs text-gray-600 mb-1">Pricing Type</div>
                              <div className="text-sm font-semibold capitalize">
                                {addon.pricing_type?.replace('_', ' ') || 'N/A'}
                              </div>
                            </div>
                            <div className="p-3 border rounded bg-gray-50">
                              <div className="text-xs text-gray-600 mb-1">Billing Cycle</div>
                              <div className="text-sm font-semibold capitalize">
                                {addon.billing_cycle || 'One-time'}
                              </div>
                            </div>
                          </div>

                          {/* Quantity Limits */}
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className="p-3 border rounded bg-orange-50">
                              <div className="text-xs text-gray-600 mb-1">Minimum Quantity</div>
                              <div className="text-lg font-bold text-orange-700">
                                {addon.minimum_quantity || 1}
                              </div>
                            </div>
                            <div className="p-3 border rounded bg-orange-50">
                              <div className="text-xs text-gray-600 mb-1">Maximum Quantity</div>
                              <div className="text-lg font-bold text-orange-700">
                                {addon.maximum_quantity === -1 ? '∞' : (addon.maximum_quantity || 'N/A')}
                              </div>
                            </div>
                          </div>

                          {/* Limit Increases - The Key Part */}
                          <div className="p-3 border rounded bg-green-50 mb-3">
                            <div className="text-sm font-semibold text-green-900 mb-2">
                              📈 Capacity Increase per Unit:
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              {Object.entries(addon.limit_increase).map(([key, value]) => (
                                <Badge key={key} variant="outline" className="bg-green-100 text-green-800 border-green-400 text-sm px-3 py-1">
                                  {key.replace(/_/g, ' ')}: <strong className="ml-1">+{value}</strong>
                                </Badge>
                              ))}
                            </div>
                          </div>

                          {/* Available Tiers */}
                          {addon.available_tiers && addon.available_tiers.length > 0 && (
                            <div className="mb-3">
                              <span className="text-sm text-gray-600">Available to Tiers:</span>
                              <div className="flex gap-2 mt-1">
                                {addon.available_tiers.map((tier) => (
                                  <Badge key={tier} variant="outline" className="text-xs capitalize">
                                    {tier}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Related Features */}
                          {addon.features && addon.features.length > 0 && (
                            <div>
                              <span className="text-sm text-gray-600">Related Features:</span>
                              <div className="flex gap-2 mt-1 flex-wrap">
                                {addon.features.map((feature) => (
                                  <Badge key={feature} variant="outline" className="bg-purple-50 text-purple-700 border-purple-300 text-xs">
                                    {formatFeatureName(feature)}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2 ml-4">
                          <Button variant="outline" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Create Tier Dialog */}
      <Dialog open={showCreateTierDialog} onOpenChange={setShowCreateTierDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Subscription Tier</DialogTitle>
            <DialogDescription>
              Define a new subscription tier with pricing and trial settings
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateTier} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create_name">Internal Name *</Label>
                <Input
                  id="create_name"
                  value={newTierForm.name}
                  onChange={(e) => setNewTierForm(prev => ({...prev, name: e.target.value.toLowerCase()}))}
                  placeholder="e.g., premium"
                  required
                />
                <p className="text-xs text-gray-500">Lowercase, no spaces</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="create_display_name">Display Name *</Label>
                <Input
                  id="create_display_name"
                  value={newTierForm.display_name}
                  onChange={(e) => setNewTierForm(prev => ({...prev, display_name: e.target.value}))}
                  placeholder="e.g., Premium Plan"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create_description">Description *</Label>
              <Textarea
                id="create_description"
                value={newTierForm.description}
                onChange={(e) => setNewTierForm(prev => ({...prev, description: e.target.value}))}
                rows={3}
                placeholder="Describe what this tier offers..."
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create_price_monthly">Monthly Price ($) *</Label>
                <Input
                  id="create_price_monthly"
                  type="number"
                  step="0.01"
                  min="0"
                  value={newTierForm.price_monthly}
                  onChange={(e) => setNewTierForm(prev => ({...prev, price_monthly: parseFloat(e.target.value) || 0}))}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="create_price_annual">Annual Price ($) *</Label>
                <Input
                  id="create_price_annual"
                  type="number"
                  step="0.01"
                  min="0"
                  value={newTierForm.price_annual}
                  onChange={(e) => setNewTierForm(prev => ({...prev, price_annual: parseFloat(e.target.value) || 0}))}
                  required
                />
              </div>
            </div>
            
            {/* Features Selection */}
            <div className="border-t pt-4 space-y-4">
              <Label className="text-lg font-medium">Features Assignment</Label>
              <p className="text-sm text-gray-600 mb-4">
                Select features to include in this tier (subject to governance rules)
              </p>
              
              <div className="space-y-3">
                <Label className="text-sm font-medium">Selected Features ({newTierForm.features?.length || 0})</Label>
                <div className="max-h-32 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                  {newTierForm.features?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {newTierForm.features.map((featureName) => (
                        <Badge 
                          key={featureName}
                          variant="outline"
                          className="cursor-pointer hover:bg-red-50"
                          onClick={() => {
                            const updatedFeatures = newTierForm.features.filter(f => f !== featureName);
                            setNewTierForm(prev => ({...prev, features: updatedFeatures}));
                          }}
                        >
                          {formatFeatureName(featureName)}
                          <XCircle className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No features selected</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Add Features</Label>
                  <Select 
                    value="" 
                    onValueChange={(featureName) => {
                      if (featureName && !newTierForm.features.includes(featureName)) {
                        const updatedFeatures = [...newTierForm.features, featureName];
                        setNewTierForm(prev => ({...prev, features: updatedFeatures}));
                      }
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select feature to add..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        // Get all available features
                        const tierFeatures = tiers.flatMap(tier => tier.features || []);
                        const governanceFeatures = [
                          ...(governance?.feature_restrictions?.professional_minimum || []),
                          ...(governance?.feature_restrictions?.business_minimum || []),
                          ...(governance?.feature_restrictions?.enterprise_only || [])
                        ];
                        const allFeatures = [...new Set([...tierFeatures, ...governanceFeatures])].sort();
                        
                        return allFeatures.map((featureName) => (
                          <SelectItem 
                            key={featureName} 
                            value={featureName}
                            disabled={newTierForm.features.includes(featureName)}
                          >
                            <div className="flex items-center gap-2">
                              <span>{formatFeatureName(featureName)}</span>
                              {newTierForm.features.includes(featureName) && (
                                <CheckCircle className="h-3 w-3 text-green-600" />
                              )}
                            </div>
                          </SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Trial Configuration */}
            <div className="border-t pt-4 space-y-4">
              <Label className="text-lg font-medium">Trial Configuration</Label>
              
              <div className="flex items-center space-x-2">
                <input
                  id="create_trial_enabled"
                  type="checkbox"
                  checked={newTierForm.trial_enabled}
                  onChange={(e) => setNewTierForm(prev => ({...prev, trial_enabled: e.target.checked}))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="create_trial_enabled" className="cursor-pointer">
                  Enable Trial Period
                </Label>
              </div>
              
              {newTierForm.trial_enabled && (
                <div className="space-y-2">
                  <Label htmlFor="create_trial_days">Trial Duration (Days)</Label>
                  <Input
                    id="create_trial_days"
                    type="number"
                    min="1"
                    value={newTierForm.trial_days}
                    onChange={(e) => setNewTierForm(prev => ({...prev, trial_days: parseInt(e.target.value) || 0}))}
                    placeholder="e.g., 7, 14, 30"
                  />
                  <p className="text-xs text-gray-500">
                    Users will get {newTierForm.trial_days} days of free trial access
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowCreateTierDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Tier</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Feature Confirmation Dialog */}
      <AlertDialog open={deleteConfirmation.isOpen} onOpenChange={(isOpen) => setDeleteConfirmation({ isOpen, featureName: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the feature "{deleteConfirmation.featureName}" and remove it from all tiers and governance rules. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmation({ isOpen: false, featureName: null })}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteFeature(deleteConfirmation.featureName)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Feature
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Tier Confirmation Dialog */}
      <AlertDialog open={deleteTierConfirmation.isOpen} onOpenChange={(isOpen) => setDeleteTierConfirmation({ isOpen, tierName: null, tierDisplayName: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Subscription Tier?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the "{deleteTierConfirmation.tierDisplayName}" tier? This will permanently remove the tier and may affect users currently subscribed to it. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteTierConfirmation({ isOpen: false, tierName: null, tierDisplayName: null })}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteTier(deleteTierConfirmation.tierName)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Tier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FeatureGovernance;